import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSpring, animated } from "@react-spring/web";
import { useInView } from 'react-intersection-observer';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, DragOverlay, DragStartEvent, DragCancelEvent, DragEndEvent } from '@dnd-kit/core';
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { SortableContext, useSortable, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import Button from "src/components/button";
import EmptyMessage from "src/components/emptyMessage";
import Slider from "src/components/slider";
import { SelectEntity } from "src/components/selectEntity ";
import { PageEntity } from "src/components/pageEntity";
import { IUseFetchProps, useFetch } from "src/components/hooks/useFetch";
import Toast from "src/components/toast";
import Tooltip from "src/components/tooltip";
import { ErrorCodes } from "src/services/error.service";

import { PageViewLayout } from "src/types/hub";
import { IPage } from "src/types/assets";
import { CreateAssetFileType } from "src/types/hub";

import { fetchAsset, createAsset, fetchPreviewAsset, invalidateSelectedAssetPages, rearrangeAssetPages, removeAssetPage, modifyAssetPages } from "src/redux/actions/assets";
import { useStoreState } from "../contentMap";

import _ from 'lodash';
import cx from 'classnames'
import styles from './styles.module.scss';
import { useAssetAdoption } from "src/redux/actions/adoption/asset";
import { COLLECTION, CREATE_ASSET, STREAM } from "src/constants/adoption/sourceTypes";
import { useHubAdoption } from "src/redux/actions/adoption/hub";
import Text from "src/components/text";

const CraftAsset = '/images/craftAsset.svg';

const CreateAsset: React.FC = () => {

  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const pathName = _.get(location, "pathname");
  const isUpdateAsset = _.includes(pathName, "update-asset");

  const selectedPages = useSelector((state) => _.get(state, "asset.selectedPages", []), _.isEqual);

  const [assetName, setAssetName] = useState<string>("");
  const [showEntityTree, setShowEntityTree] = useState<boolean>(true);
  const [showEntityBrowser, setShowEntityBrowser] = useState<boolean>(false);
  const [selectedEntity, setSelectedEntity] = useState<any>();

  const inputRef = useRef<HTMLInputElement>(null);

  const assetId = _.get(params, "id");
  const assetItem = useSelector((state) => _.get(state, "asset.item", null), _.isEqual);
  const { loading, succeeded, item, error } = useStoreState(`asset.createAsset`, false);
  const assetPages = useSelector((state) => _.get(state, `asset.assetsPreview[${assetId || ""}]`), _.isEqual) as any;
  const { loading : isPreviewLoading , succeeded : isPreviewFetched, item : previewItem, error : previewFetchError } = assetPages || {};

  const {trackPageAdded, trackDestinationSelected} = useAssetAdoption();
  const { trackAssetCreatedEvent} = useHubAdoption();

  useEffect(() => {
    if (selectedEntity && newAssetType) {
      if (selectedEntity.entityType === "stream") {
        trackDestinationSelected({ assetType: newAssetType, destinationType: STREAM, sourceCta: CREATE_ASSET, collectionName : "", streamName: selectedEntity.name });
      }
      if (selectedEntity.entityType === "collection") {
        trackDestinationSelected({ assetType: newAssetType, destinationType: COLLECTION, sourceCta: CREATE_ASSET, collectionName : selectedEntity.name, streamName: "" });
      }
    }
  },[ selectedEntity ])

  const newAssetType = useMemo(() => { 

    if (isUpdateAsset) {
      var extension = _.get(assetItem, "metadata.extension");
      if (extension === "pdf" || extension === "pptx") {
        return _.upperCase(extension);
      }
    }
    else {
      return _.upperCase(_.get(params, "type", ""));
    }

  }, [pathName, assetItem])

  const createNewAssetProps: IUseFetchProps = {
    action: createAsset,
    loading,
    succeeded,
    response: item,
    error
  }

  const { handleFetch: createNewAsset } = useFetch(createNewAssetProps);

  const updateAssetProps: IUseFetchProps = {
    action: modifyAssetPages,
    loading,
    succeeded,
    response: item,
    error
  }

  const { handleFetch: updateAsset } = useFetch(updateAssetProps);

  const fetchPreviewProps: IUseFetchProps = {
    action: fetchPreviewAsset,
    loading: isPreviewLoading,
    succeeded: isPreviewFetched,
    response: previewItem,
    error: previewFetchError
  }

  const { handleFetch: fetchAssetPreview } = useFetch(fetchPreviewProps);

  useEffect(() => {
    if (isUpdateAsset) {
      setAssetName(_.get(assetItem, "name", ""));
    }

    return (() => {
      invalidateCreateAsset();
    })

  }, [assetItem])

  useEffect(() => {
    if (isUpdateAsset && assetId) {

      if (!assetItem) {
        dispatch(fetchAsset({ assetId }));
      }

      if (!previewItem) {
        fetchAssetPreview({ id:  assetId }).then((response) => {
          const pages = _.get(response, "pages", []);
          const pagesWithAssetId = pages.map((page: any) => {
            return {
              ...page,
              asset: assetId,
              assetName: _.get(assetItem, "name", "")
            }
          });
          dispatch(rearrangeAssetPages(pagesWithAssetId));
        }).catch((error) => {
          console.log("Error", error)
        })
      }
      else {
        if (previewItem.asset === assetId) {
          const pages = _.get(previewItem, "pages", []);
          const pagesWithAssetId = pages.map((page: any) => {
            return {
              ...page,
              asset: assetId,
              assetName: _.get(assetItem, "name", "")
            }
          });
          dispatch(rearrangeAssetPages(pagesWithAssetId));
        }
      }
    }

  }, [assetId, assetItem, pathName, previewItem])

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAssetName(e.target.value)
  }

  const invalidateCreateAsset = () => {
    setAssetName("");
    setSelectedEntity(null);
    dispatch(invalidateSelectedAssetPages());
  }

  const handleBackClick = () => {
    if (isUpdateAsset) {
      navigate(`/assets/${assetId}`);
    }
    else {
      navigate("/hub");
    }
  }

  const handleCreateAsset = () => {

    const pages = selectedPages.map(page => ({
      asset: _.get(page, "asset", ""),
      index: _.get(page, "index", "")
    }));

    const requestBody = {
      title: assetName,
      pages: pages,
      entityId: _.get(selectedEntity, "id"),
      entity: _.get(selectedEntity, "entityType")
    }

    createNewAsset({ type: newAssetType, requestBody }).then((response) => {
      if(newAssetType){
        trackAssetCreatedEvent({
          asset_type: newAssetType as CreateAssetFileType,
          collection_name: selectedEntity.entityType === "collection" ?  _.get(selectedEntity, "name") : "",
          stream_name:selectedEntity.entityType === "stream" ?  _.get(selectedEntity, "name") : ""
        })
      }

      invalidateCreateAsset();
      toast(<Toast.Progress 
        heading={"In Progress"}
        message={`Crafting Stand By`} />, {
        autoClose: 3000
      })

      const entityType = selectedEntity.entityType + 's';

      if(entityType === "collection_sections"){
        navigate(`/assets/${response.id}`);
      }else{
        navigate(`/assets/${response.id}?source=${entityType}/${selectedEntity.id}`);
      }

    }).catch((error) => {
      console.log("Error", error);
      const errorCode = _.get(error, "errors[0].code", null);
      const errorMessage = _.get(error, "errors[0].message", '');
      if (errorCode === ErrorCodes.STORAGE_LIMIT_REACHED) {
        toast(<Toast.warning heading="Warning" message={errorMessage} />, {
          position: "bottom-right",
          autoClose: false,
        });
      }
    });
  }

  const handleConfirmClick = () => {
    if (isUpdateAsset) {
      const pages = selectedPages.map(page => ({
        asset: _.get(page, "asset", ""),
        index: _.get(page, "index", "")
      }));

      const body = {
        assetId: assetId,
        assetType: newAssetType,
        requestBody: pages
      }

      updateAsset(body).then(() => {
        trackPageAdded();
        if(assetId){
          const request = {
            assetId: assetId,
          };
          dispatch(fetchAsset(request));
          dispatch(fetchPreviewAsset({id:assetId}));
        }
 
        navigate(`/assets/${assetId}`);
        invalidateCreateAsset();
        setSelectedEntity(null);
        toast(<Toast.Sucess message={`Asset updated successfully`} />, {
          autoClose: 3000
        })
      }).catch((error) => {
        console.log("Error", error);
        const errorCode = _.get(error, "errors[0].code", null);
        const errorMessage = _.get(error, "errors[0].message", '');
        if (errorCode === ErrorCodes.STORAGE_LIMIT_REACHED) {
          toast(<Toast.warning heading="Warning" message={errorMessage} />, {
            position: "bottom-right",
            autoClose: false,
          });
        }
      });

    } else {
      const trimmedAssetName = _.trim(assetName);
      if (_.size(trimmedAssetName) === 0) {
        toast(<Toast.Failure message={`You forgot to name your new asset`} />, {
          autoClose: 3000
        })
      }
      else {
        setShowEntityBrowser(true);
      }
    }
  }

  const opacityAnimation = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: { friction: 50 },
  })

  return (
    <animated.div style={opacityAnimation} className={styles.createAssetContainer}>
      <div className={cx(styles.entityTree, {
        [styles.hideEntityTree]: !showEntityTree
      })}>
        <div onClick={handleBackClick} className={styles.backContainer}>
          <i className="paperflite-left-arrow"></i>
          <Text text={isUpdateAsset ? _.get(assetItem, "name", "") : "content hub"} className={styles.title} />
        </div>

        <div className={styles.headerContainer}>
          <div className={styles.header}> {isUpdateAsset ? "update" : "create"} new asset </div>
          <div className={styles.subText}> Pick and mix pages from your existing assets </div>
        </div>

        <PageEntity fileType={newAssetType === "PDF" ? CreateAssetFileType.PDF : CreateAssetFileType.PPTX} style={null} width={375} onSelection={() => ""} />

      </div>

      <i className={cx(styles.toggleArrow, "paperflite-dropdown", {
        [styles.rotateArrow]: !showEntityTree
      })} onClick={() => setShowEntityTree(!showEntityTree)} />
      <div className={cx(styles.pageContainer, {
        [styles.fullWidth]: !showEntityTree
      })}>

        <div className={styles.topBar}>

          {!isUpdateAsset && <div
            onMouseEnter={() => inputRef?.current?.focus()}
            className={styles.inputContainer}>
            {!showEntityTree && <span onClick={handleBackClick} className={styles.backContainer}>
              <i className="paperflite-left-arrow"></i>
            </span>}
            <input value={assetName} ref={inputRef} type="text" placeholder="Name your new document" onChange={handleQueryChange} />
            <span className={styles.pages}> /{_.size(selectedPages)} pages</span>
          </div>}
          {isUpdateAsset &&
            <div className={styles.staticName}>
              <span className={styles.name}>{assetName}<span className={styles.pages}>/{_.size(selectedPages)} pages</span></span>
            </div>
          }
          <div className={styles.buttonContainer}>
            <Button
              disabled={_.size(selectedPages) === 0 || loading}
              icon="paperflite-tick"
              text="Confirm"
              variant="primary"
              onClick={handleConfirmClick}
            />
            <Button
              text="Cancel"
              variant="secondary"
              onClick={() => navigate("/hub")}
            />
          </div>
        </div>

        <div className={styles.body}>

          {_.size(selectedPages) === 0 && <div className={styles.emptyMessageContainer}>
            <div className={styles.emptyMessage}>
              <EmptyMessage
                width={600}
                image={CraftAsset}
                heading="Build from your best content"
                message="Search and select documents from your content library. Pick the pages you need and combine them into a new document."
              />
            </div>
            <div className={styles.placeholder} />
          </div>}

          {_.size(selectedPages) > 0 && <AssetContainer isFullWidth={!showEntityTree} fileType={newAssetType}/>}

        </div>

      </div>

      <Slider.Dialog width="380px" show={showEntityBrowser} onClose={() => {
        setShowEntityBrowser(false)
        setSelectedEntity(null);
      }}>

        <Slider.Header title="a star is born - your new file! where's its address?" message="Choose where you want the asset to be saved" />
        <Slider.Body>
          <SelectEntity style={{}} width={315} onSelection={(entity) => setSelectedEntity(entity)} selectedEntity={selectedEntity} />
        </Slider.Body>

        <Slider.Actions>
          {selectedEntity && <p className={styles.entityName}>Saving your files to "{_.get(selectedEntity, "name")}"</p>}
          <Button icon="paperflite-tick"
            text={'Confirm'}
            variant='primary'
            disabled={loading || !selectedEntity}
            onClick={handleCreateAsset}
          />
          <Button text="Cancel"
            variant='secondary'
            disabled={loading}
            onClick={() => {
              setShowEntityBrowser(false)
              setSelectedEntity(null);
            }}
          />
        </Slider.Actions>


      </Slider.Dialog>

    </animated.div>
  )
}

interface AssetContainerProps {
  isFullWidth: boolean,
  fileType: string | undefined
}

interface CreateAssetContextTypes {
  isFullWidth: boolean,
  activeLayout: PageViewLayout,
  activePageInViewIndex: number,
  scrollToPage: (id: string | number) => void,
  setActivePageInView: any,
  fileType: string | undefined
}

const CreateAssetContext = React.createContext<CreateAssetContextTypes>({
  isFullWidth: false,
  activeLayout: PageViewLayout.list_view,
  activePageInViewIndex: 0,
  scrollToPage: () => { },
  setActivePageInView: () => { },
  fileType: CreateAssetFileType.PDF
});

const AssetContainer: React.FC<AssetContainerProps> = (props) => {

  const isFullWidth = props.isFullWidth;

  const selectedPages = useSelector((state) => _.get(state, "asset.selectedPages", []), _.isEqual);

  const [activeLayout, setActiveLayout] = useState<PageViewLayout>(PageViewLayout.list_view);
  const [activePageInView, setActivePageInView] = useState<string>("");
  const [activePageInViewIndex, setActivePageInViewIndex] = useState<number>(0);

  const isFullScreenView = activeLayout === PageViewLayout.fullscreen;
  const isGridView = activeLayout === PageViewLayout.grid_view;
  const isListView = activeLayout === PageViewLayout.list_view;

  useEffect(() => {
    const index = _.findIndex(selectedPages, (item) => _.get(item, 'background.id') === activePageInView);
    setActivePageInViewIndex(index + 1);
  }, [activePageInView, selectedPages])

  const scrollToPage = (id: string | number) => {
    const pageId = `page_${id}`;
    const pageElement = document.getElementById(pageId);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: "smooth" })
    }
  }

  const opacityAnimation = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: { friction: 50 },
  })

  const pageCountAnimation = useSpring({
    display: "inline-flex",
    opacity: isFullScreenView ? 1 : 0,
    overflow: "hidden",
    config: { tension: 150 },
  })

  const contextValue = {
    isFullWidth,
    activeLayout,
    activePageInViewIndex,
    scrollToPage,
    setActivePageInView,
    fileType : props.fileType
  }

  return (
    <CreateAssetContext.Provider value={contextValue}>
      <animated.div style={opacityAnimation} className={styles.pageViewInnerContainer}>

        <div className={styles.pageViewer}>
          <div className={cx(styles.activePageView, {
            [styles.fullscreenView]: isFullScreenView,
            [styles.fullWidthView]: isFullWidth,
            [styles.hideActivePageView]: isGridView
          })}>
            {_.size(selectedPages) > 0 && selectedPages.map((page) => {
              return <PageView page={page} key={_.get(page, "background.id")} setActivePageInView={setActivePageInView} fullscreenView={isFullScreenView} />
            })}

          </div>

          <DraggablePageCards />

        </div>

        <span className={cx(styles.actionButtonContainer, {
          [styles.fullscreen]: isFullScreenView,
          [styles.grid]: isGridView
        })}>

          <animated.span style={pageCountAnimation} className={cx(styles.pageOuterContainer, { [styles.pageCountFullScreen]: isFullScreenView })}>
            <span className={styles.page}>Page <span className={styles.currentPage}>{activePageInViewIndex}</span>/{_.size(selectedPages)}</span>
          </animated.span>
          <i onClick={() => setActiveLayout(PageViewLayout.list_view)}
            className={cx("paperflite-page-view", {
              [styles.active]: isListView
            })} />
          <i onClick={() => setActiveLayout(PageViewLayout.fullscreen)}
            className={cx("paperflite-capture", {
              [styles.active]: isFullScreenView
            })} />
          <i onClick={() => setActiveLayout(PageViewLayout.grid_view)}
            className={cx("paperflite-categories", {
              [styles.active]: isGridView
            })} />
        </span>
      </animated.div>
    </CreateAssetContext.Provider>
  )
}

// selected page renderer
const PageView: React.FC<{ page: any, setActivePageInView: any, fullscreenView: any }> = ({ page, setActivePageInView, fullscreenView }) => {

  const { ref, inView } = useInView({
    threshold: fullscreenView ? 0.4 : 0.2,
  });

  useEffect(() => {
    if (inView) {
      setActivePageInView(_.get(page, 'background.id'))
    }
  }, [inView]);

  return (<img
    ref={ref}
    id={`page_${_.get(page, 'background.id')}`}
    src={_.get(page, 'background.full')}
  />)
}

const DraggablePageCards = () => {

  const dispatch = useDispatch();
  const selectedPages = useSelector((state) => _.get(state, "asset.selectedPages", []), _.isEqual);

  const { activeLayout, scrollToPage, fileType } = React.useContext(CreateAssetContext);

  const [activeDragPageId, setActiveDragPageId] = useState<string | number>();
  const isFullScreenView = activeLayout === PageViewLayout.fullscreen;
  const isGridView = activeLayout === PageViewLayout.grid_view;

  const sensors = useSensors(
    useSensor(PointerSensor)
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragPageId(_.get(event, "active.id"))
    scrollToPage(_.get(event, "active.id"))
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragPageId("")

    const { active, over } = event;
    scrollToPage(_.get(event, "over.id", ""))

    if (active && over && _.get(active, "id", "") !== _.get(over, "id", "")) {
      const oldIndex = _.findIndex(selectedPages, (item) => _.get(item, 'background.id') === active.id);
      const newIndex = _.findIndex(selectedPages, (item) => _.get(item, 'background.id') === over.id);
      const updatedArray = arrayMove(selectedPages, oldIndex, newIndex);
      dispatch(rearrangeAssetPages(updatedArray));
    }
  };

  const handleDragCancel = (event: DragCancelEvent) => {
    setActiveDragPageId("");
  }

  const getActiveDragPage = useMemo(() => {
    const activeDragPage = _.find(selectedPages, (item) => _.get(item, "background.id", "") === activeDragPageId)
    const activeDragPageIndex = _.findIndex(selectedPages, (item) => _.get(item, "background.id", "") === activeDragPageId)
    return { activeDragPage, activeDragPageIndex };
  }, [activeDragPageId])

  const { activeDragPage, activeDragPageIndex } = getActiveDragPage;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={selectedPages.map(page => _.get(page, "background.id"))} strategy={rectSortingStrategy}>
        <div className={cx(styles.allPageContainer, {
          [styles.hideAllPage]: isFullScreenView,
          [styles.gridView]: isGridView
        })}>
          {selectedPages.map((page, index) => (
            <PageCard key={_.get(page, "background.id")} page={page} index={index} activeDragPageId={activeDragPageId} />
          ))}
        </div>
      </SortableContext>

      <DragOverlay modifiers={[restrictToWindowEdges]}>
        {activeDragPage && <div className={cx(styles.pageCard, fileType ? styles[fileType.toLowerCase()] : '', styles.dragging)}>
          <p> {activeDragPageIndex < 9 ? 0 : ""}{activeDragPageIndex + 1}</p>
          <div className={styles.overlay} />
          <img
            id={`page_${_.get(activeDragPage, 'background.id')}`}
            src={_.get(activeDragPage, 'background.full')}
          />
        </div>}
      </DragOverlay>

    </DndContext>
  );
};

const PageCard: React.FC<any> = ({ page, index, activeDragPageId }) => {

  const dispatch = useDispatch();
  const pageId = _.get(page, 'background.id')

  const { activePageInViewIndex, fileType } = useContext(CreateAssetContext);
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: pageId });
  const cardRef = useRef<HTMLDivElement | null>(null);

  const transformStyles = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleDeletePage = (page: IPage) => {
    dispatch(removeAssetPage(page));
  }

  return <div className={cx(styles.pageCard, fileType ? styles[fileType.toLowerCase()] : '', {
    [styles.dragging]: activeDragPageId === pageId})}>
    <i onClick={(e) => handleDeletePage(page)} className={cx(styles.closeIcon, "paperflite-close")} />
    <Tooltip content={page.assetName} placement="auto">
      <i className={cx("paperflite-info", styles.sourceTooltip)} />
    </Tooltip>
    <div ref={(node) => { setNodeRef(node); cardRef.current = node; }}
      style={transformStyles} {...attributes} {...listeners}
    >
      <p className={cx({ [styles.active]: index + 1 === activePageInViewIndex })}> {index < 9 ? 0 : ""}{index + 1}</p>
      <div className={styles.overlay} />
      <img
        id={`page_${pageId}`}
        src={_.get(page, 'background.full')}
      />
    </div>
  </div>
}

export default CreateAsset